import {
  firstValueFrom,
  identity,
  Observable,
  of,
  OperatorFunction,
  pipe,
} from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { CurrentShow } from '../../flows/showcase/functions/useCurrentShow/useCurrentShow';
import {
  LiveStreamListingApi,
  StoreListingApi,
  StorePublicListingApi,
} from '../../generated/api';
import { Listing, ListingStatus } from '../../models/ClientModels';
import { shareReplay } from '../../utils/rxExtensions';
import { currentUserId$ } from '../auth/utils';
import { EntityRef, entityRefSelect } from '../EntityRef';
import {
  DocumentReference,
  DocumentSnapshot,
  firestore,
  Query,
  QuerySnapshot,
} from '../firebase/firebaseAccess';
import {
  createObservableQuery,
  docDataWithId,
  docQuery,
  ObservableQuery,
  snapshotToDataWithId,
  WithID,
} from '../firebase/rxfireExtensions';
import {
  listDimsToApiServerListDims,
  listingVariantToApiServerStoreListingVariantRequest,
  listShippingSurchargeRuleToApiServerListShippingSurchargeRule,
  listWeightToApiServerListWeight,
  storeListingToApiServerStoreListingRequest,
} from './types';

type ExtraRxOperator = OperatorFunction<
  DocumentSnapshot<Listing>[],
  DocumentSnapshot<Listing>[]
>;

export class ListingFirestoreHelper {
  constructor(
    private liveStreamListingSvc: LiveStreamListingApi,
    private storePublicListingSvc: StorePublicListingApi,
    private storeListingSvc: StoreListingApi,
  ) {}

  private getStoreListingsRawQuery(storeId: string) {
    return firestore().collection('stores').doc(storeId).collection('listings');
  }

  private getStoreListingRawQuery(storeId: string, listingId: string) {
    return firestore()
      .collection('stores')
      .doc(storeId)
      .collection('listings')
      .doc(listingId);
  }

  getStoreListings(
    storeId: string,
    extraRxOperator?: ExtraRxOperator,
  ): ObservableQuery<Listing> {
    return this.createObservableQuery(
      this.getStoreListingsRawQuery(storeId).orderBy('createdAt', 'desc'),
      extraRxOperator,
    );
  }
  getStoreListingsUpdateQuery(
    storeId: string,
    extraRxOperator?: ExtraRxOperator,
  ): ObservableQuery<Listing> {
    return this.createObservableQuery(
      this.getStoreListingsRawQuery(storeId).orderBy('updatedAt', 'desc'),
      extraRxOperator,
    );
  }

  async createStoreListing(
    storeId: string,
    listing: Listing,
  ): Promise<{ id: string }> {
    const res =
      await this.storeListingSvc.storeListingApiControllerCreateStoreListing({
        storeId,
        createStoreListingRequest:
          storeListingToApiServerStoreListingRequest(listing),
      });

    return { id: res.id };
  }

  async updateStoreListing(
    storeId: string,
    listingId: string,
    newListing: Listing,
    oldListing: Listing,
  ): Promise<void> {
    await this.storeListingSvc.storeListingApiControllerEditStoreListing({
      storeId,
      listingId,
      editStoreListingRequest: {
        ...storeListingToApiServerStoreListingRequest(newListing),
        currentQuantity: oldListing.quantity,
        currentVariants: oldListing.variants?.map(
          listingVariantToApiServerStoreListingVariantRequest,
        ),
      },
    });
  }

  async deleteStoreListing(storeId: string, listingId: string): Promise<void> {
    await this.storeListingSvc.storeListingApiControllerDeleteStoreListing({
      storeId,
      listingId,
    });
  }

  async getStoreListing(
    storeId: string,
    listingId: string,
  ): Promise<WithID<Listing> | undefined> {
    return await firstValueFrom(
      docDataWithId(this.getStoreListingRawQuery(storeId, listingId)),
    );
  }

  // --------- public listing ---------

  getStorePublicListingsRawQuery(storeId: string) {
    return firestore()
      .collection('stores')
      .doc(storeId)
      .collection('publicListings');
  }
  getStorePublicListings(storeId: string): ObservableQuery<Listing> {
    return this.createObservableQuery(
      this.getStorePublicListingsRawQuery(storeId).orderBy('createdAt', 'desc'),
    );
  }
  getStorePublicListingsUpdateQuery(storeId: string): ObservableQuery<Listing> {
    return this.createObservableQuery(
      this.getStorePublicListingsRawQuery(storeId).orderBy('updatedAt', 'desc'),
    );
  }

  private getStorePublicListingRef(
    storeId: string,
    listingId: string,
  ): DocumentReference<Listing> {
    return firestore()
      .collection('stores')
      .doc(storeId)
      .collection('publicListings')
      .doc(listingId);
  }

  getStorePublicListing(
    storeId: string,
    listingId: string,
  ): Observable<null | DocumentSnapshot<Listing>> {
    return docQuery(this.getStorePublicListingRef(storeId, listingId)).pipe(
      map(listing =>
        this.isListingSnapshotStillAvailable(listing) ? listing : null,
      ),
    );
  }

  async addStorePublicListingFromStoreListings(
    storeId: string,
    listingIds: string[],
  ): Promise<void> {
    await this.storePublicListingSvc.storePublicListingApiControllerBatchAddStorePublicListing(
      {
        storeId,
        batchAddStorePublicListingRequest: {
          listingIds,
        },
      },
    );
  }

  async deleteStorePublicListings(
    storeId: string,
    listingIds: string[],
  ): Promise<void> {
    await this.storePublicListingSvc.storePublicListingApiControllerBatchDeleteStorePublicListing(
      {
        storeId,
        batchDeleteStorePublicListingRequest: {
          listingIds,
        },
      },
    );
  }

  currentAccountStoreReelQuery = currentUserId$.pipe(
    switchMap(uid =>
      uid == null
        ? of([])
        : this.getStorePublicListings(uid)().pipe(map(res => res.docs)),
    ),
    shareReplay(1),
  );

  // --------- live stream listing ---------

  getLiveStreamListingsRawQuery(liveStreamId: string) {
    return firestore()
      .collection('liveStreams')
      .doc(liveStreamId)
      .collection('listings');
  }
  getLiveStreamListings(liveStreamId: string): ObservableQuery<Listing> {
    return this.createObservableQuery(
      this.getLiveStreamListingsRawQuery(liveStreamId).orderBy(
        'createdAt',
        'desc',
      ),
    );
  }
  getLiveStreamListingsUpdateQuery(
    liveStreamId: string,
  ): ObservableQuery<Listing> {
    return this.createObservableQuery(
      this.getLiveStreamListingsRawQuery(liveStreamId).orderBy(
        'updatedAt',
        'desc',
      ),
    );
  }

  async addLiveStreamListingFromInventoryListings(
    liveStreamId: string,
    listingIds: string[],
  ): Promise<void> {
    await this.liveStreamListingSvc.livestreamListingApiControllerAddListingsInBatch(
      {
        liveStreamId,
        addListingsToLiveStreamInBatchDto: {
          listingIds,
        },
      },
    );
  }

  async createLiveStreamListing(
    liveStreamId: string,
    _storeId: string,
    listing: Listing,
  ): Promise<void> {
    // the name of the API is weird, but the functionality is corrected
    await this.liveStreamListingSvc.livestreamListingApiControllerCreateLiveStreamListingBySeller(
      {
        liveStreamId,
        createLiveStreamListingRequest: {
          ...listing,
          dims:
            listing.dims == null
              ? undefined
              : listDimsToApiServerListDims(listing.dims),
          weight:
            listing.weight == null
              ? undefined
              : listWeightToApiServerListWeight(listing.weight),

          variants: listing.variants?.map(v => {
            return {
              ...v,
              dims:
                v.dims == null
                  ? undefined
                  : listDimsToApiServerListDims(v.dims),
              weight:
                v.weight == null
                  ? undefined
                  : listWeightToApiServerListWeight(v.weight),
            };
          }),
          shippingSurcharge:
            listing.shippingSurcharge == null
              ? undefined
              : listShippingSurchargeRuleToApiServerListShippingSurchargeRule(
                  listing.shippingSurcharge,
                ),
          shippingFee: listing.shippingFee ?? undefined,
          maximumPurchaseQuantity: listing.maximumPurchaseQuantity ?? undefined,
        },
      },
    );
  }

  async updateLiveStreamListing(
    liveStreamId: string,
    _storeId: string,
    listingId: string,
    newListing: Listing,
    oldListing: Listing,
  ): Promise<void> {
    // the name of the API is weird, but the functionality is corrected
    await this.liveStreamListingSvc.livestreamListingApiControllerEditStoreListing(
      {
        liveStreamId,
        listingId,
        editLiveStreamListingRequest: {
          ...{
            ...newListing,
            dims:
              newListing.dims == null
                ? undefined
                : listDimsToApiServerListDims(newListing.dims),
            weight:
              newListing.weight == null
                ? undefined
                : listWeightToApiServerListWeight(newListing.weight),

            variants: newListing.variants?.map(v => {
              return {
                ...v,
                dims:
                  v.dims == null
                    ? undefined
                    : listDimsToApiServerListDims(v.dims),
                weight:
                  v.weight == null
                    ? undefined
                    : listWeightToApiServerListWeight(v.weight),
              };
            }),
          },
          shippingSurcharge:
            newListing.shippingSurcharge == null
              ? undefined
              : listShippingSurchargeRuleToApiServerListShippingSurchargeRule(
                  newListing.shippingSurcharge,
                ),
          shippingFee: newListing.shippingFee ?? undefined,
          maximumPurchaseQuantity:
            newListing.maximumPurchaseQuantity ?? undefined,
          currentQuantity: oldListing.quantity,
          currentVariants: oldListing.variants?.map(
            listingVariantToApiServerStoreListingVariantRequest,
          ),
        },
      },
    );
  }

  async deleteLiveStreamListing(
    liveStreamId: string,
    listingId: string,
  ): Promise<void> {
    await this.liveStreamListingSvc.livestreamListingApiControllerDeleteLiveStreamListing(
      {
        liveStreamId,
        listingId,
      },
    );
  }

  getLiveStreamListing(
    liveStreamId: string,
    listingId: string,
  ): Observable<null | DocumentSnapshot<Listing>> {
    return docQuery(
      firestore()
        .collection('liveStreams')
        .doc(liveStreamId)
        .collection('listings')
        .doc(listingId),
    ).pipe(
      map(listing =>
        this.isListingSnapshotStillAvailable(listing) ? listing : null,
      ),
    );
  }

  // ---------- utility -----------

  getListing(
    showRef: ListingFirestoreHelperSupportedShowRef,
    listingId: string,
  ): Observable<null | Listing> {
    return entityRefSelect(showRef, {
      liveShow: r => this.getLiveStreamListing(r.liveStreamId, listingId),
      upcomingShow: r =>
        CurrentShow.fromUpcomingShow$(r.upcomingShowId).pipe(
          switchMap(show =>
            this.getStorePublicListing(show.upcomingShow.userId, listingId),
          ),
        ),
      seller: r => this.getStorePublicListing(r.sellerId, listingId),
    }).pipe(map(doc => (doc == null ? doc : snapshotToDataWithId(doc))));
  }

  getAbsolutelyEmptyListingQuery(): ObservableQuery<Listing> {
    return this.createObservableQuery(
      firestore()
        .collection('stores')
        .doc('a random and should never exist id')
        .collection('publicListings'),
    );
  }

  isListingSnapshotStillAvailable(snap: DocumentSnapshot<Listing>): boolean {
    if (!snap.exists) return false;
    if (snap.data()!.status === ListingStatus.Archived) return false;
    return true;
  }

  private createObservableQuery(
    query: Query<Listing>,
    extraRxOperator?: ExtraRxOperator,
  ): ObservableQuery<Listing> {
    return createObservableQuery(
      query,
      pipe(
        map((snapshots: QuerySnapshot<Listing>) =>
          snapshots.docs.filter(snap =>
            this.isListingSnapshotStillAvailable(snap),
          ),
        ),
        extraRxOperator ?? identity,
      ),
    );
  }
}

export type ListingFirestoreHelperSupportedShowRef =
  | EntityRef.LiveShow
  | EntityRef.UpcomingShow
  | EntityRef.Seller;
